import React, { useMemo, useState } from "react";
import {
  VStack,
  Text,
  Alert,
  AlertDescription,
  Icon,
  Box,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { FiBell } from "react-icons/fi";
import { AgendaHeader } from "@components/Agenda";
import { NewsCard } from "./NewsCard";
import { NewsTabs } from "./NewsTabs";
import { NewsMessage } from "./NewsMessage";
import { TrainersToolkit } from "./TrainersToolkit";
import { FeatureLab } from "./FeatureLab";
import {
  useDismissPassiveNewsMutation,
  useGetPassiveNewsQuery,
} from "@store/api/news";

const NewsView = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [message, setMessage] = useState("");
  const {
    data: passiveNews,
    isFetching,
    isError,
    refetch,
  } = useGetPassiveNewsQuery();
  const [dismissPassiveNews] = useDismissPassiveNewsMutation();

  const newsItems = useMemo(() => {
    if (!passiveNews?.items?.length) {
      return [];
    }

    return [...passiveNews.items].sort((a, b) => {
      if (b.dayOffset === a.dayOffset) {
        return a.id.localeCompare(b.id);
      }
      return b.dayOffset - a.dayOffset;
    });
  }, [passiveNews]);

  const handleNewsTabChange = (tabId) => {
    setActiveTab(tabId);
    setMessage(""); // Clear message when switching tabs
  };

  const handleDismiss = async (newsId) => {
    try {
      await dismissPassiveNews(newsId).unwrap();
    } catch (error) {
      setMessage("We couldn't dismiss that news right now. Please try again.");
    }
  };

  const filteredNews = useMemo(() => {
    if (activeTab === "all") {
      return newsItems;
    }

    if (activeTab === "host-hub") {
      return newsItems.filter((item) => item.tabId === "host-hub");
    }

    return [];
  }, [activeTab, newsItems]);

  const renderNewsList = () => {
    if (isFetching && !passiveNews) {
      return (
        <Center height="100%">
          <Spinner color="purple.500" size="sm" />
        </Center>
      );
    }

    if (isError) {
      return (
        <Alert
          status="error"
          borderRadius="10px"
          border="1px solid"
          borderColor="#D0CCD5"
          p={6}
        >
          <AlertDescription
            fontSize="14px"
            color="granite"
            fontFamily="Geist, sans-serif"
          >
            We couldn't load the latest news.
            <Text
              as="button"
              color="blue.500"
              fontWeight="600"
              cursor="pointer"
              onClick={refetch}
            >
              Retry
            </Text>
          </AlertDescription>
        </Alert>
      );
    }

    if (!filteredNews.length) {
      return (
        <Alert
          status="success"
          bg="blue.25"
          borderRadius="10px"
          border="1px solid"
          borderColor="#D0CCD5"
          p={6}
          textAlign="center"
        >
          <VStack spacing={3}>
            <Icon as={FiBell} boxSize={8} color="blue.500" />
            <Text
              fontSize="16px"
              fontWeight="600"
              color="dark.500"
              fontFamily="Geist, sans-serif"
              mb={1}
            >
              You’re all caught up!
            </Text>
            <AlertDescription
              fontSize="14px"
              color="granite"
              fontFamily="Geist, sans-serif"
            >
              Check back later for new tips and ideas.
            </AlertDescription>
          </VStack>
        </Alert>
      );
    }

    return (
      <VStack spacing={3} align="stretch">
        {filteredNews.map((news) => (
          <NewsCard
            key={news.id}
            title={news.notification}
            onDismiss={() => handleDismiss(news.id)}
            actionButton={
              news.needsFormEntry && activeTab === "all"
                ? {
                    text: "Go to Feature Lab →",
                    onClick: () => handleNewsTabChange("feature-lab"),
                  }
                : undefined
            }
          />
        ))}
      </VStack>
    );
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box flexShrink={0}>
        <AgendaHeader
          title="News Home"
          buttonText=""
          showSparkles={false}
          onSmartAgendaClick={() => {}}
          disabled={true}
        />

        <NewsTabs activeTab={activeTab} onTabChange={handleNewsTabChange} />

        <NewsMessage message={message} />
      </Box>

      <Box flex="1" minH="0" overflow="hidden" marginTop="20px">
        {activeTab === "all" && (
          <Box height="100%" overflowY="auto">
            {renderNewsList()}
          </Box>
        )}

        {activeTab === "host-hub" && (
          <Box height="100%" overflowY="auto">
            {renderNewsList()}
          </Box>
        )}

        {activeTab === "trainers-toolkit" && (
          <Box height="100%" overflowY="auto">
            <TrainersToolkit
              onSuccess={() =>
                setMessage(
                  "🎓 Trainer's Toolkit coming soon. Thanks for signing up!"
                )
              }
            />
          </Box>
        )}

        {activeTab === "feature-lab" && (
          <Box height="100%" overflowY="auto">
            <FeatureLab
              onSuccess={() =>
                setMessage(
                  "🤗 🙏 🫶 Thanks for helping shape Townhall! Keep the suggestions coming."
                )
              }
            />
          </Box>
        )}

        {activeTab !== "all" &&
          activeTab !== "host-hub" &&
          activeTab !== "trainers-toolkit" &&
          activeTab !== "feature-lab" && (
            <Box height="100%" overflowY="auto">
              <Alert
                status="info"
                bg="blue.25"
                borderRadius="10px"
                border="1px solid"
                borderColor="#D0CCD5"
                p={6}
                textAlign="center"
              >
                <VStack spacing={3}>
                  <Icon as={FiBell} boxSize={8} color="blue.500" />
                  <Text
                    fontSize="16px"
                    fontWeight="600"
                    color="dark.500"
                    fontFamily="Geist, sans-serif"
                    mb={1}
                  >
                    Coming Soon
                  </Text>
                  <AlertDescription
                    fontSize="14px"
                    color="granite"
                    fontFamily="Geist, sans-serif"
                  >
                    This section will be available soon. Check back later for
                    updates!
                  </AlertDescription>
                </VStack>
              </Alert>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default NewsView;
